import { useApiQuery } from '~/hooks';
import {
  getExtractionJobApi,
  getExtractionTaskApi,
  getExtractionTasksApi,
  getChartsDataApi,
  getStatsDataApi,
  getTasksStatsDataApi,
  getTaskCreditUsageDataApi,
  getAnalyticsStatsApi,
  getAnalyticsChartsDataApi,
  getTopPerformingTasksApi,
  getValidationCostApi,
  ExtractionTask,
  ChartsData,
  StatsData,
  TasksStatsData,
  TaskCreditUsageData,
  AnalyticsStatsData,
  AnalyticsChartsData,
  TopPerformingTask,
  ValidationCostData,
} from '../repository';
import { keepPreviousData, UseQueryOptions } from '@tanstack/react-query';

export const EXTRACTION_QUERY_KEYS = {
  EXTRACTION_JOB: 'extraction-job',
  EXTRACTION_TASK: 'extraction-task',
  EXTRACTION_TASKS: 'extraction-tasks',
  CHARTS_DATA: 'charts-data',
  STATS_DATA: 'stats-data',
  TASKS_STATS_DATA: 'tasks-stats-data',
  TASK_CREDIT_USAGE_DATA: 'task-credit-usage-data',
  ANALYTICS_STATS: 'analytics-stats',
  ANALYTICS_CHARTS: 'analytics-charts',
  TOP_PERFORMING_TASKS: 'top-performing-tasks',
  VALIDATION_COST: 'validation-cost',
};

/**
 * Query hook to fetch extraction job by job ID
 */
export const useGetExtractionJobQuery = (
  jobId: string,
  options?: UseQueryOptions<ExtractionTask>,
) => {
  return useApiQuery({
    ...options,
    queryKey: [EXTRACTION_QUERY_KEYS.EXTRACTION_JOB, jobId],
    queryFn: () => getExtractionJobApi(jobId),
    enabled: options?.enabled !== false && !!jobId,
  });
};

/**
 * Query hook to fetch extraction task by task ID
 */
export const useGetExtractionTaskQuery = (
  taskId: string,
  options?: Omit<UseQueryOptions<ExtractionTask>, 'queryKey' | 'queryFn'>,
) => {
  return useApiQuery({
    ...options,
    queryKey: [EXTRACTION_QUERY_KEYS.EXTRACTION_TASK, taskId],
    queryFn: () => getExtractionTaskApi(taskId),
    enabled: options?.enabled !== false && !!taskId,
  });
};

/**
 * Query hook to fetch user's extraction tasks
 */
export const useGetExtractionTasksQuery = (
  params?: {
    status?: string;
    search?: string;
    limit?: number;
    offset?: number;
  },
  options?: UseQueryOptions<{
    tasks: ExtractionTask[];
    total: number;
  }>,
) => {
  return useApiQuery({
    ...options,
    queryKey: [EXTRACTION_QUERY_KEYS.EXTRACTION_TASKS, params],
    queryFn: () => getExtractionTasksApi(params),
    placeholderData: keepPreviousData,
  });
};

/**
 * Query hook to fetch dashboard charts data
 * Returns weekly activity and monthly jobs data
 */
export const useGetChartsDataQuery = (
  options?: UseQueryOptions<ChartsData>,
) => {
  return useApiQuery({
    ...options,
    queryKey: [EXTRACTION_QUERY_KEYS.CHARTS_DATA],
    queryFn: () => getChartsDataApi(),
  });
};

/**
 * Query hook to fetch dashboard stats data
 * Returns stats with month-over-month comparisons
 */
export const useGetStatsDataQuery = (options?: UseQueryOptions<StatsData>) => {
  return useApiQuery({
    ...options,
    queryKey: [EXTRACTION_QUERY_KEYS.STATS_DATA],
    queryFn: () => getStatsDataApi(),
  });
};

/**
 * Query hook to fetch tasks stats data
 * Returns total tasks, completed tasks, credits used, and active tasks
 */
export const useGetTasksStatsDataQuery = (
  options?: UseQueryOptions<TasksStatsData>,
) => {
  return useApiQuery({
    ...options,
    queryKey: [EXTRACTION_QUERY_KEYS.TASKS_STATS_DATA],
    queryFn: () => getTasksStatsDataApi(),
  });
};

/**
 * Query hook to fetch task credit usage data
 * Returns daily task completion and credit consumption for last 7 days
 */
export const useGetTaskCreditUsageDataQuery = (
  options?: UseQueryOptions<TaskCreditUsageData[]>,
) => {
  return useApiQuery({
    ...options,
    queryKey: [EXTRACTION_QUERY_KEYS.TASK_CREDIT_USAGE_DATA],
    queryFn: () => getTaskCreditUsageDataApi(),
  });
};

/**
 * Query hook to fetch analytics stats data
 * Returns comprehensive analytics stats based on date range
 * @param range - Date range: '7d', '30d', '90d', or 'all' (default: '30d')
 */
export const useGetAnalyticsStatsQuery = (
  range: '7d' | '30d' | '90d' | 'all' = '30d',
  options?: UseQueryOptions<AnalyticsStatsData>,
) => {
  return useApiQuery({
    ...options,
    queryKey: [EXTRACTION_QUERY_KEYS.ANALYTICS_STATS, range],
    queryFn: () => getAnalyticsStatsApi(range),
  });
};

/**
 * Query hook to fetch analytics charts data
 * Returns chart data for analytics page based on date range
 * @param range - Date range: '7d', '30d', '90d', or 'all' (default: '30d')
 */
export const useGetAnalyticsChartsQuery = (
  range: '7d' | '30d' | '90d' | 'all' = '30d',
  options?: UseQueryOptions<AnalyticsChartsData>,
) => {
  return useApiQuery({
    ...options,
    queryKey: [EXTRACTION_QUERY_KEYS.ANALYTICS_CHARTS, range],
    queryFn: () => getAnalyticsChartsDataApi(range),
  });
};

/**
 * Query hook to fetch top performing tasks
 * Returns top performing tasks based on date range
 * @param range - Date range: '7d', '30d', '90d', or 'all' (default: '30d')
 */
export const useGetTopPerformingTasksQuery = (
  range: '7d' | '30d' | '90d' | 'all' = '30d',
  options?: UseQueryOptions<TopPerformingTask[]>,
) => {
  return useApiQuery({
    ...options,
    queryKey: [EXTRACTION_QUERY_KEYS.TOP_PERFORMING_TASKS, range],
    queryFn: () => getTopPerformingTasksApi(range),
  });
};

/**
 * Query hook to fetch validation cost for an extraction task
 * @param taskId - Task ID to calculate validation cost for
 */
export const useGetValidationCostQuery = (
  taskId: string,
  options?: Omit<UseQueryOptions<ValidationCostData>, 'queryKey' | 'queryFn'>,
) => {
  return useApiQuery({
    ...options,
    queryKey: [EXTRACTION_QUERY_KEYS.VALIDATION_COST, taskId],
    queryFn: () => getValidationCostApi(taskId),
    enabled: options?.enabled !== false && !!taskId,
  });
};
